/**
 * Politica de contraseñas del sistema.
 *
 * Replica las reglas de backend/users/validators.py (PasswordPolicyValidator) para
 * poder avisarle al usuario mientras escribe y evitar el round trip. La validacion
 * que manda sigue siendo la del backend: si estas reglas quedan desactualizadas, lo
 * unico que pasa es que el error llega desde la API en vez de mostrarse local.
 */

const PASSWORD_MIN_LENGTH = 12;

// Cuantas contraseñas anteriores recuerda el backend (PASSWORD_HISTORY_SIZE en
// users/validators.py). Aca solo se usa para el texto informativo: esta regla no se
// puede validar en el navegador, el historial son hashes que solo tiene el backend.
const PASSWORD_HISTORY_SIZE = 24;

// Puntuacion ASCII imprimible, el mismo set que PASSWORD_SPECIAL_CHARS del backend.
// El espacio queda afuera a proposito: no cuenta como caracter especial.
const PASSWORD_SPECIAL_CHARS = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

// Subconjunto que se muestra en los mensajes: listar los 32 caracteres los hace ilegibles.
const PASSWORD_SPECIAL_CHARS_EJEMPLO = "! @ # $ % & * ? -";

// Se incluyen las vocales acentuadas y la eñe para no obligar a escribir en ASCII.
const RE_UPPERCASE = /[A-ZÁÉÍÓÚÜÑ]/;
const RE_LOWERCASE = /[a-záéíóúüñ]/;
const RE_DIGIT = /[0-9]/;

const hasSpecialChar = password =>
    password.split('').some(char => PASSWORD_SPECIAL_CHARS.indexOf(char) !== -1);

const PASSWORD_HELP_TEXT = 'La contraseña debe tener al menos ' + PASSWORD_MIN_LENGTH +
    ' caracteres e incluir una letra mayúscula, una letra minúscula, un número y un ' +
    'carácter especial (por ejemplo: ' + PASSWORD_SPECIAL_CHARS_EJEMPLO + ').';

/**
 * Evalua la contraseña regla por regla.
 *
 * Devuelve siempre las cinco reglas con su estado (`ok`), para poder dibujar el
 * checklist completo; `label` es el texto corto del checklist y `detail` el mensaje
 * largo, identico al que devolveria el backend.
 */
function getPasswordRules(password) {
    const value = password || '';

    return [
        {
            code: 'password_too_short',
            label: 'Al menos ' + PASSWORD_MIN_LENGTH + ' caracteres',
            detail: 'La contraseña debe tener al menos ' + PASSWORD_MIN_LENGTH + ' caracteres.',
            ok: value.length >= PASSWORD_MIN_LENGTH,
        },
        {
            code: 'password_no_upper',
            label: 'Una letra mayúscula',
            detail: 'La contraseña debe incluir al menos una letra mayúscula.',
            ok: RE_UPPERCASE.test(value),
        },
        {
            code: 'password_no_lower',
            label: 'Una letra minúscula',
            detail: 'La contraseña debe incluir al menos una letra minúscula.',
            ok: RE_LOWERCASE.test(value),
        },
        {
            code: 'password_no_digit',
            label: 'Un número',
            detail: 'La contraseña debe incluir al menos un número.',
            ok: RE_DIGIT.test(value),
        },
        {
            code: 'password_no_special',
            label: 'Un carácter especial (' + PASSWORD_SPECIAL_CHARS_EJEMPLO + ')',
            detail: 'La contraseña debe incluir al menos un carácter especial (por ejemplo: ' +
                PASSWORD_SPECIAL_CHARS_EJEMPLO + ').',
            ok: hasSpecialChar(value),
        },
    ];
}

/**
 * Devuelve los errores de la contraseña en el formato { code, detail } que espera la
 * prop `error` de ParadigmaLabeledInput (el mismo que manda el backend). Array vacio
 * significa que cumple la politica.
 */
function validatePassword(password) {
    return getPasswordRules(password)
        .filter(rule => !rule.ok)
        .map(rule => ({ code: rule.code, detail: rule.detail }));
}

/**
 * True si la contraseña cumple con toda la politica.
 */
function isPasswordValid(password) {
    return validatePassword(password).length === 0;
}

module.exports = {
    PASSWORD_MIN_LENGTH,
    PASSWORD_HISTORY_SIZE,
    PASSWORD_SPECIAL_CHARS,
    PASSWORD_SPECIAL_CHARS_EJEMPLO,
    PASSWORD_HELP_TEXT,
    getPasswordRules,
    validatePassword,
    isPasswordValid,
};



// WEBPACK FOOTER //
// ./src/functions/passwordPolicy.js